'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoginDialog } from './LoginDialog';
import { installLoginQueryConsumer, useLoginDialogStore, type LoginQueryRouter } from '@/lib/auth/login-dialog';
import { reportClientError } from '@/lib/client-errors';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureFlag, useFeatureFlagsStore } from '@/lib/stores/feature-flags-store';
import { captureReferralAttribution, loadStoredReferralAttribution, saveStoredReferralAttribution } from '@/lib/referrals/attribution';
import { loadPendingReferralClaim, rememberReferralClaim } from '@/lib/referrals/claim-receipt';
import { resolveLoginDestination } from '@/lib/referrals/presentation';
import type { StoredReferralAttribution } from '@/types/referrals';

function ActiveLogin({ params, onClose }: { params: Record<string, string>; onClose: () => void }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const wechatQrLoginEnabled = useFeatureFlag('auth_wechat_qr_login');
  const wechatBindMode = params.bind === 'wechat';
  const redirectStartedRef = useRef(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [storedAttribution, setStoredAttribution] = useState<StoredReferralAttribution | null>(null);

  useEffect(() => {
    setStoredAttribution(loadStoredReferralAttribution());
  }, []);

  const beginAuthenticatedRedirect = useCallback(() => {
    if (redirectStartedRef.current) return;
    redirectStartedRef.current = true;
    const activeUserId = useAuthStore.getState().user?.id;
    const pendingClaim = loadPendingReferralClaim();
    const pendingCode = pendingClaim && (!pendingClaim.userId || pendingClaim.userId === activeUserId) ? pendingClaim.code : null;
    const claimCode = pendingCode || params.ref || storedAttribution?.referralCode
      || loadStoredReferralAttribution()?.referralCode;
    if (claimCode) rememberReferralClaim(claimCode, activeUserId);
    const effectiveRedirectTo = resolveLoginDestination(params.redirect ?? null, undefined, window.location.origin);
    window.location.replace(effectiveRedirectTo);
  }, [params, storedAttribution?.referralCode]);

  useEffect(() => {
    let active = true;
    void useFeatureFlagsStore.getState().initFlags();
    void useAuthStore.getState().checkSession().then(() => {
      if (active) setSessionChecked(true);
    }).catch(() => {
      // The sign-in form remains usable when restoring a previous session fails.
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (sessionChecked && isAuthenticated && accessToken && !wechatBindMode) beginAuthenticatedRedirect();
  }, [sessionChecked, isAuthenticated, accessToken, wechatBindMode, beginAuthenticatedRedirect]);

  useEffect(() => {
    const normalizedCode = params.ref?.trim().toUpperCase();
    if (!normalizedCode) return;
    const campaignKey = params.campaign_key || params.campaign;
    const sourceChannel = params.source_channel || params.channel || params.source;
    let cancelled = false;
    setStoredAttribution(saveStoredReferralAttribution({ referralCode: normalizedCode, campaignKey, sourceChannel }));
    captureReferralAttribution({
      referralCode: normalizedCode, campaignKey, sourceChannel,
      landingPath: `${window.location.pathname}${window.location.search}`,
      utmSource: params.utm_source, utmMedium: params.utm_medium, utmCampaign: params.utm_campaign,
    }).then((attribution) => {
      if (!cancelled && attribution) setStoredAttribution(attribution);
    }).catch(() => {
      if (!cancelled) setStoredAttribution(loadStoredReferralAttribution());
    });
    return () => { cancelled = true; };
  }, [params]);

  return <LoginDialog open onClose={onClose} onAuthenticated={beginAuthenticatedRedirect}
    initialMethod={params.method === 'wechat' ? 'wechat' : 'email'}
    storedAttribution={storedAttribution} wechatQrLoginEnabled={wechatQrLoginEnabled} />;
}

/** Mounted once per router, inside its locale scope; never replaces the underlying page. */
export function LoginModalHost({queryRouter}: {queryRouter?: LoginQueryRouter | null} = {}) {
  const request = useLoginDialogStore((state) => state.request);
  const close = useLoginDialogStore((state) => state.close);
  useEffect(() => {
    return installLoginQueryConsumer(queryRouter, (reason) => {
      const error = reason instanceof Error ? reason : new Error('Login route cleanup failed');
      void reportClientError({error, extra: {source: 'login_route_cleanup'}}).catch(() => {});
    });
  }, [queryRouter]);
  return request && typeof document !== 'undefined'
    ? createPortal(<ActiveLogin key={request.id} params={request.params} onClose={close} />, document.body)
    : null;
}
